/**
 * notify_policy.repository.ts — 사업부별 업무 알림 켜고 끄기(조직 on/off, 12 §4-2 알림).
 *
 * Firestore: `tenants/{t}/units/{biz}/settings/notify_policy` = { kinds: { ship_start: true, … }, updated_at, updated_by }
 *   - 처음부터 법인 경로(중앙 레미콘도 tenants/joongang/units/rmc/…) — domain/tenantPath.ts TENANT_PATH_FROM_START.
 *   - 쓰기: admin · 그 법인 관리자(rules §5-6 초안). 읽기: admin · 그 법인 소속.
 *   - 함수(collectRecipientTokens)는 kinds[type] === false 이면 그 사업부 알림을 아무에게도 보내지 않는다(미설정 = 보냄).
 * 키는 사업부 종류 알림(domain/session.ts PUSH_TYPE_SCOPE = "unit")만 저장한다.
 */
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "./firebase";
import { settingsDocPath, type SettingsAt } from "@/domain/tenantPath";
import { PUSH_NOTIFY_TYPES, type PushNotifyType } from "@/domain/push_copy";
import { PUSH_TYPE_SCOPE, type NotifyPolicy } from "@/domain/session";

const KEY = "notify_policy";

/** 사업부 종류 알림 목록(표시·저장 순서) — PUSH_TYPE_SCOPE 가 정본 */
export const UNIT_NOTIFY_TYPES: readonly PushNotifyType[] = PUSH_NOTIFY_TYPES.filter((t) => PUSH_TYPE_SCOPE[t] === "unit");
const UNIT_SET: ReadonlySet<string> = new Set<string>(UNIT_NOTIFY_TYPES);

function parseKinds(v: unknown): NotifyPolicy["kinds"] {
  const out: NotifyPolicy["kinds"] = {};
  if (!v || typeof v !== "object") return out;
  for (const [k, on] of Object.entries(v as Record<string, unknown>)) {
    // 런타임 화이트리스트 — 모르는 종류·그룹 종류(signup)는 버린다
    if (UNIT_SET.has(k) && typeof on === "boolean") out[k as PushNotifyType] = on;
  }
  return out;
}

export const notifyPolicyRepo = {
  /** 구독 — 문서가 없으면 null(아직 한 번도 저장하지 않음) */
  watch(at: SettingsAt, cb: (p: NotifyPolicy | null) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      doc(db, settingsDocPath(KEY, at)),
      (snap) => cb(snap.exists() ? { kinds: parseKinds(snap.data().kinds) } : null),
      onError,
    );
  },

  /** 저장 — 사업부 종류 전부를 명시적으로 기록(빠진 키가 "보냄"으로 해석되지 않게) */
  async save(at: SettingsAt, kinds: NotifyPolicy["kinds"], uid?: string): Promise<void> {
    const full: Record<string, boolean> = {};
    for (const t of UNIT_NOTIFY_TYPES) full[t] = kinds[t] !== false;
    await setDoc(
      doc(db, settingsDocPath(KEY, at)),
      { kinds: full, updated_at: serverTimestamp(), updated_by: uid ?? "" },
      { merge: true },
    );
  },
};
